import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  screeningChannel,
  type MemberSummary,
  type ScreeningSeatMap,
  type SeatOccupant,
} from '@movienight/shared';
import { cancelReservation, getSeatMap, reserveSeat } from '../lib/api';
import { useSocket } from '../lib/SocketProvider';

/**
 * One screening's seat map, live.
 *
 * ## Optimism, and where it stops
 *
 * A tap moves the marker immediately — a seat map that waits for a round trip
 * before showing you sat down feels broken on a phone. But unlike the hype
 * flame, a booking is genuinely refusable: two people can want the same
 * beanbag, and the database will pick one (see `reservations.ts`). So the
 * optimistic state is *corrected loudly* rather than quietly: a rejection
 * refetches the whole map and says what happened, because the interesting part
 * is not that your tap failed, it is that somebody else is sitting there.
 *
 * ## Events say a seat changed; they do not say who
 *
 * `seat_booked` carries a `userId` and nothing else — no nickname, no avatar —
 * which is the same rule the rest of the socket layer follows. So the change
 * is applied structurally at once, with the occupant left as a placeholder,
 * and a debounced refetch fills in the face. A room filling up in the two
 * minutes after an announcement produces a burst of these, and one refetch per
 * seat would be the one expensive thing on this screen.
 */

export type SeatMapState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; map: ScreeningSeatMap };

/** Trailing window for putting names to the seats that just changed. */
const FACES_MS = 400;

export interface SeatMapApi {
  state: SeatMapState;
  /** The seat currently waiting on the server, so its marker can say so. */
  pendingSeatId: string | null;
  /** Set by a refusal — "neko te je pretekao" — and cleared by the next tap. */
  problem: string | null;
  take: (seatId: string) => void;
  release: () => void;
  dismissProblem: () => void;
}

export function useSeatMap(screeningId: string, me: MemberSummary): SeatMapApi {
  const { socket, onResync } = useSocket();
  const [state, setState] = useState<SeatMapState>({ status: 'loading' });
  const [pendingSeatId, setPendingSeatId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const current = useRef<ScreeningSeatMap | null>(null);
  const facesTimer = useRef<number | undefined>(undefined);

  const apply = useCallback((map: ScreeningSeatMap) => {
    current.current = map;
    setState({ status: 'ready', map });
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await getSeatMap(screeningId));
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Ne mogu da učitam mesta.',
      });
    }
  }, [screeningId, apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => onResync(() => void refresh()), [onResync, refresh]);

  useEffect(() => socket.subscribe(screeningChannel(screeningId)), [socket, screeningId]);

  const scheduleFaces = useCallback(() => {
    window.clearTimeout(facesTimer.current);
    facesTimer.current = window.setTimeout(() => void refresh(), FACES_MS);
  }, [refresh]);

  /** Rewrite the occupant list in place, leaving everything else alone. */
  const patch = useCallback(
    (fn: (taken: SeatOccupant[]) => SeatOccupant[]) => {
      const map = current.current;
      if (!map) return;
      const taken = fn(map.taken);
      apply({
        ...map,
        taken,
        mySeatId: taken.find((t) => t.user.id === me.id)?.seatId ?? null,
      });
    },
    [apply, me.id],
  );

  // --- socket ---------------------------------------------------------------

  useEffect(
    () =>
      socket.on((event) => {
        if (event.channel !== screeningChannel(screeningId)) return;

        if (event.name === 'seat_booked') {
          const { seatId, userId } = event.payload;
          patch((taken) => [
            // One seat per person: whoever this is, they are not where they
            // were. Dropping their old row here is what stops the same face
            // appearing twice for the moment before the refetch lands.
            ...taken.filter((t) => t.seatId !== seatId && t.user.id !== userId),
            {
              seatId,
              placedByHost: false,
              // A placeholder until the refetch below says who. `me` is the
              // one case we can fill in without asking.
              user: userId === me.id ? me : { id: userId, nickname: '', avatarUrl: null },
            },
          ]);
          if (userId !== me.id) scheduleFaces();
          return;
        }

        if (event.name === 'seat_freed') {
          const { seatId } = event.payload;
          patch((taken) => taken.filter((t) => t.seatId !== seatId));
          return;
        }

        if (event.name === 'seat_swapped') {
          // Swap offers are not built yet (§8). When they are, the two halves
          // arrive together and the map has to move both at once — so this
          // refetches rather than pretending a pair of booked events.
          void refresh();
        }
      }),
    [socket, screeningId, patch, scheduleFaces, refresh, me],
  );

  // --- acting ---------------------------------------------------------------

  const take = useCallback(
    (seatId: string) => {
      const map = current.current;
      if (!map || pendingSeatId) return;
      setProblem(null);
      setPendingSeatId(seatId);

      patch((taken) => [
        ...taken.filter((t) => t.seatId !== seatId && t.user.id !== me.id),
        { seatId, placedByHost: false, user: me },
      ]);

      void reserveSeat(screeningId, seatId)
        .catch((err: unknown) => {
          setProblem(err instanceof Error ? err.message : 'Nije uspelo.');
          // The map on screen is now a guess that turned out wrong, and the
          // interesting part is who is actually sitting there.
          return refresh();
        })
        .finally(() => setPendingSeatId(null));
    },
    [screeningId, me, patch, refresh, pendingSeatId],
  );

  const release = useCallback(() => {
    const map = current.current;
    if (!map?.mySeatId || pendingSeatId) return;
    const seatId = map.mySeatId;
    setProblem(null);
    setPendingSeatId(seatId);

    patch((taken) => taken.filter((t) => t.user.id !== me.id));

    void cancelReservation(screeningId)
      .catch((err: unknown) => {
        setProblem(err instanceof Error ? err.message : 'Nije uspelo.');
        return refresh();
      })
      .finally(() => setPendingSeatId(null));
  }, [screeningId, me.id, patch, refresh, pendingSeatId]);

  useEffect(() => () => window.clearTimeout(facesTimer.current), []);

  return useMemo(
    () => ({
      state,
      pendingSeatId,
      problem,
      take,
      release,
      dismissProblem: () => setProblem(null),
    }),
    [state, pendingSeatId, problem, take, release],
  );
}
